import { useContext } from 'react';
import { AuthContext } from './AuthProvider.jsx';

/**
 * useAuth - Hook to access authentication context
 *
 * Provides access to authentication state and methods.
 * Must be used within an AuthProvider.
 *
 * @returns {Object} Authentication context
 * @returns {Object} currentUser - Firebase user object
 * @returns {Object} userProfile - User profile from Platform API
 * @returns {boolean} loading - Whether authentication is still loading
 * @returns {Function} login - Login with email/password
 * @returns {Function} signup - Create new account
 * @returns {Function} logout - Sign out
 * @returns {Function} refreshUserProfile - Refresh user profile from API
 *
 * @example
 * function MyComponent() {
 *   const { currentUser, userProfile, login, logout } = useAuth();
 *
 *   if (!currentUser) {
 *     return <div>Not logged in</div>;
 *   }
 *
 *   return <div>Welcome {userProfile?.email}</div>;
 * }
 */
export function useAuth() {
    const context = useContext(AuthContext);

    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
}
